import React, { createContext, useState, useContext, useEffect } from "react";
import { auth } from "../firebase";
import { onAuthStateChanged } from "firebase/auth";

const AuthContext = createContext(null);

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

/**
 * Single source of truth for the logged-in user.
 * - user: Firebase User object or null
 * - userId: user?.uid ?? null (use for Firestore: where("userId", "==", userId) or where("uid", "==", userId) in users)
 * - loading: true until the first auth state is known
 */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    // Persistence is configured in firebase.js via initializeAuth (native) / getAuth (web).
    // Do not call setPersistence here — it can hang forever on Capacitor iOS.
    const unsubscribe = onAuthStateChanged(auth, (authUser) => {
      if (!cancelled) {
        setUser(authUser);
        setLoading(false);
      }
    });
    // Safety: if auth never resolves (rare IndexedDB hang), stop spinning.
    const timeoutId = setTimeout(() => {
      if (!cancelled) setLoading(false);
    }, 8000);
    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
      unsubscribe();
    };
  }, []);

  const userId = user?.uid ?? null;

  const value = {
    user,
    userId,
    loading,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export default AuthProvider;
